<template>
    <article>
        <p>
            There seems to be even more buzz concerning the three, four, or possibly more pillars of observability.
            In the end it's just metrics, logs, and traces. Some are now referring to profiles as the fourth pillar.
        </p>

        <p>
            All the experts focus mostly on how to gather and store telemetry data.
            However, I have hardly ever heard any thoughts on how to use all this data to pinpoint issues.
            I believe, the most important thing is to know what to do when you've been notified that there is something wrong with your system:
        </p>

        <ul>
            <li>Should I look at metrics? Which ones exactly? I have thousands of them!</li>
            <li>Or maybe logs or traces? Where should I start?</li>
        </ul>

        <p>
            Perhaps the reason why there isn't a broader conversation about this crucial part of troubleshooting is that
            some people believe it doesn't make sense to suggest anything without knowing the specifics of a particular system,
            as not all systems are built the same.
        </p>

        <p>But, is that really the case?</p>

        <p>
            Let's conduct a thought experiment.
            Imagine that a friend is calling you for help about an issue in their system which you know nothing about.
            If you really wanted to help, what hypotheses would you propose about the possible cause of the problem?
            I've been asking many engineers this question, and it's surprising to find that over 80% of hypotheses are the same.
        </p>

        <p>
            Although it may seem like there are endless possible causes of an outage, try writing them down one by one.
            After a few minutes, you'll likely find it challenging to come up with something completely new to add to your list.
        </p>

        <p>
            We built Coroot under the belief that, in most cases, the root cause of an outage can be detected automatically.
            <a href="https://github.com/coroot/coroot" target="_blank">Coroot</a> is an open-source zero-instrumentation
            observability tool that turns telemetry data into answers about app issues and how to fix them.
        </p>

        <p>
            In this post, I'll try to convince you that there are much fewer potential scenarios of problems than it may seem at first glance.
        </p>

        <p>
            Let's make a comprehensive list of the factors that may potentially cause an application to become unavailable,
            responding with errors, or operate slower than usual.
            Firstly, let's list the possible types of problems.
            I could only come up with the following six types.
            While I might have overlooked something, I don't believe it's possible to double this list.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="1200px" class="ma-auto" src="/static/img/blog/pillars-of-observability/types_of_possible_causes.svg" />
        </div>

        <p>
            Now, let's dig into each of these types and explore the various failure scenarios and how to identify them.
            The biggest challenge in automatically detecting a specific cause is that all checks must be carried out within the context of a particular application.
            This means that it's essential to have knowledge of all application instances, the nodes they run on,
            and the services they interact with, among other relevant factors.
        </p>

        <h2>Lack of CPU time</h2>

        <p>
            The CPU time available on a given node is a finite resource that must be shared among the containers running on it.
            As a result, these containers compete for their fair share of CPU time.
            When the CPU becomes overwhelmed or an application container has reached its CPU limit,
            the app's ability to process user requests may be significantly impacted.
        </p>

        <FailureScenario correlation>
            <template #scenario>
                Insufficient CPU capacity on related nodes has occurred due to the application or other processes running on them
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="container_resources_cpu_delay_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="node_resources_cpu_usage_seconds_total" exporter="node-agent"/>
                </div>
            </template>
            <template #threshold>
                CPU usage of a node > 80%
            </template>
            <template #correlation>
                cpu_delay is correlating with the affected SLIs
                <v-img eager max-width="800px" src="/static/img/blog/pillars-of-observability/cpu_delay.png" />
            </template>
        </FailureScenario>

        <FailureScenario correlation>
            <template #scenario>
                A container has reached its CPU limit and has been throttled by the system (limited in CPU time for a period of time)
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="container_resources_cpu_throttled_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_resources_cpu_usage_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_resources_cpu_limit_cores" exporter="node-agent"/>
                </div>
            </template>
            <template #threshold>
                usage > 80% of limit
            </template>
            <template #correlation>
                throttled_time is correlating with the affected SLIs
            </template>
        </FailureScenario>

        <p>
            Once a CPU-related issue is detected, you can use
            <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'profiling', item: 'pyroscope' }}">
                a continuous profiler
            </router-link> to analyze any unexpected spike in CPU usage down to the precise line of code.
        </p>

        <v-card max-width="80%" class="text-center pa-2">
            <v-img eager class="ma-auto mb-6" src="/static/img/docs/coroot-ce/pyroscope-comparison.png" />
        </v-card>


        <h2>Application instances are not available</h2>

        <p>
            So, what are the possible scenarios that could lead to one or more application instances becoming unavailable?
            Everyone is familiar with the manual investigation process, which involves using <var>kubectl</var>, logs, and some container metrics.
        </p>

        <div class="pa-5 my-6">
            <v-img eager max-width="1000px" class="ma-auto" src="/static/img/blog/pillars-of-observability/instance_issues.svg" />
        </div>

        <p>
            However, the most common failure scenarios can be detected automatically:
        </p>

        <FailureScenario>
            <template #scenario>
                The nodes where application instances were running became unavailable
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="node_uptime_seconds" exporter="node-agent"/>
                </div>
            </template>
            <template #threshold>
                == NaN
            </template>
        </FailureScenario>

        <FailureScenario>
            <template #scenario>
                The instances cannot be deployed due to issues related to invalid specification or container images
            </template>
            <template #metrics>
                kube_pod_status_phase{phase=~"Pending|Failed"}
            </template>
            <template #threshold>
                == 1
            </template>
        </FailureScenario>

        <FailureScenario>
            <template #scenario>
                Insufficient capacity to place application instances
            </template>
            <template #metrics>
                kube_pod_status_scheduled{condition="False"}
            </template>
            <template #threshold>
                == 1
            </template>
        </FailureScenario>

        <FailureScenario>
            <template #scenario>
                The instances are crashing due to fatal errors in the application (panic)
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="container_restarts_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_log_messages_total" alias='container_log_messages_total{level=~"error|critical"}' exporter="node-agent" />
                </div>
            </template>
            <template #threshold>
                increase > 0
            </template>
        </FailureScenario>

        <FailureScenario>
            <template #scenario>
                The instances have been terminated by the OOM (Out of Memory) killer
            </template>
            <template #metrics>
                <MetricLink name="container_oom_kills_total" exporter="node-agent"/>
            </template>
            <template #threshold>
                increase > 0
            </template>
        </FailureScenario>

        <FailureScenario>
            <template #scenario>
                Readiness probes are failed due to upstream services or databases
            </template>
            <template #metrics>
                kube_pod_status_ready{condition="false"}
            </template>
            <template #threshold>
                == 1
            </template>
        </FailureScenario>

        <h2>Storage issues</h2>

        <p>
            A disk subsystem is a crucial component for most stateful applications such as database servers.
            There are two primary types of issues with storage that can affect any stateful application:
        </p>

        <ul>
            <li>Inability to perform read or write requests</li>
            <li>I/O latency: slow disk access times can cause delays in reading or writing data</li>
        </ul>



        <FailureScenario correlation>
            <template #scenario>
                High I/O latency due to IOPS throttling or a throughput limit
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="node_resources_disk_(reads|writes)_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="node_resources_disk_(read|write)_time_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="node_resources_disk_io_time_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="aws_rds_io_util_percent" exporter="aws-agent" />
                </div>
                <div>
                    <MetricLink name="aws_rds_io_await_seconds" exporter="aws-agent" />
                </div>
                <div>
                    <MetricLink name="aws_rds_io_ops_per_second" exporter="aws-agent" />
                </div>
                <div>
                    <MetricLink name="aws_rds_io_bytes_per_second" exporter="aws-agent" />
                </div>
                <div>
                    <MetricLink name="aws_rds_storage_provisioned_iops" exporter="aws-agent" />
                </div>
            </template>
            <template #threshold>
                I/O time % > 80%
            </template>
            <template #correlation>
                io_latency is correlating with the affected SLIs
                <v-img eager max-width="800px" src="/static/img/blog/pillars-of-observability/io_latency.png" />
            </template>
        </FailureScenario>

        <FailureScenario>
            <template #scenario>
                Insufficient disk space
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="container_resources_disk_size_bytes" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_resources_disk_used_bytes" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="aws_rds_fs_total_bytes" exporter="aws-agent"/>
                </div>
                <div>
                    <MetricLink name="aws_rds_fs_used_bytes" exporter="aws-agent"/>
                </div>
            </template>
            <template #threshold>
                used disk space > 80%
            </template>
        </FailureScenario>

        <h2>Application runtime issues</h2>

        <p>
            Although this class of problems is highly diverse and the specific scenarios can vary depending on the
            architecture of each individual application, there are some common issues that can be automatically identified.
            Furthermore, analyzing application logs can help pinpoint errors relevant to a particular outage.
        </p>

        <ul>
            <li>Application-specific errors</li>
            <li>Lock contention</li>
            <li>Thread/connection pool capacity</li>
            <li>Garbage collection / stop-the-world pauses</li>
        </ul>


        <FailureScenario correlation>
            <template #scenario>
                Application-specific errors
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="container_log_messages_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="aws_rds_log_messages_total" exporter="aws-agent"/>
                </div>
            </template>
            <template #threshold>
                the number of error in the application logs > 0
            </template>
            <template #correlation>
                the number of error in the log is correlating with the affected SLIs
                <v-img eager max-width="900px" src="/static/img/blog/pillars-of-observability/log_patterns.png" />
            </template>
        </FailureScenario>

        <FailureScenario correlation>
            <template #scenario>
                JVM: a java application has been stopped for a significant amount of time due to safepoint operations
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="container_jvm_safepoint_time_seconds" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_jvm_safepoint_sync_time_seconds" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_jvm_gc_time_seconds" exporter="node-agent"/>
                </div>
            </template>
            <template #threshold>
                safepoint_time > 50ms per second
            </template>
            <template #correlation>
                safepoint_time is correlating with the affected SLIs
            </template>
        </FailureScenario>

        <FailureScenario correlation>
            <template #scenario>
                Postgres: a postgres server is not accepting connections due to the <var>max_connections</var> limit
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="pg_connections" exporter="pg-agent"/>
                </div>
                <div>
                    <MetricLink name="pg_setting" alias='pg_setting{name="max_connections"}' exporter="pg-agent"/>
                </div>
                <div>
                    <MetricLink name="container_log_messages_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="aws_rds_log_messages_total" exporter="aws-agent"/>
                </div>
            </template>
            <template #threshold>
                the number of connections > 90% of max_connections
            </template>
            <template #correlation>
                the number of rejected connections is correlating with the affected SLIs
            </template>
        </FailureScenario>

        <FailureScenario correlation>
            <template #scenario>
                Postgres: some queries are waiting for locks
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="pg_connections" alias='pg_connections{wait_event_type="Lock"}' exporter="pg-agent"/>
                </div>
                <div>
                    <MetricLink name="pg_lock_awaiting_queries" exporter="pg-agent"/>
                </div>
            </template>
            <template #threshold>
                the number of queries awaiting locks > 0
            </template>
            <template #correlation>
                the number of queries awaiting locks is correlating with the affected SLIs
            </template>
        </FailureScenario>


        <h2>Issues with upstream services and databases</h2>

        <p>
            If an application depends on other services or databases to process requests,
            any degradation in the performance of those components will inevitably impact the application's own performance.
        </p>

        <p>
            Distributed tracing is a common method used to gain insight into the interactions between services.
            While traces are effective for analyzing individual requests, aggregated metrics are better suited for automated analysis.
            Moreover, it's worth noting that traces may not always be effective for identifying network issues that occur between services.
        </p>

        <FailureScenario correlation>
            <template #scenario>
                An upstream service or database is serving errors
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="container_http_requests_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_postgres_queries_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_redis_queries_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_memcached_queries_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_mysql_queries_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_mongo_queries_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_kafka_requests_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_cassandra_queries_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_rabbitmq_messages_total" exporter="node-agent"/>
                </div>
            </template>
            <template #threshold>
                the upstream service or database is not meeting its availability SLO
            </template>
            <template #correlation>
                the number of failed requests is correlating with the affected SLIs
            </template>
        </FailureScenario>

        <FailureScenario correlation>
            <template #scenario>
                An upstream service is performing slowly
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="container_http_requests_duration_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_postgres_queries_duration_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_redis_queries_duration_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_memcached_queries_duration_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_mysql_queries_duration_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_mongo_queries_duration_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_kafka_requests_duration_seconds_total" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_cassandra_queries_duration_seconds_total" exporter="node-agent"/>
                </div>
            </template>
            <template #threshold>
                the upstream service or database is not meeting its latency SLO
            </template>
            <template #correlation>
                the latency of the upstream service is correlating with the affected SLIs
            </template>
        </FailureScenario>

        <FailureScenario correlation>
            <template #scenario>
                High network latency to the upstream service or database
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="container_net_latency_seconds" exporter="node-agent"/>
                </div>
            </template>
            <template #threshold>
                network round-trip time (RTT) > 10ms
            </template>
            <template #correlation>
                the network latency to the upstream service is correlating with the affected SLIs
                <v-img eager max-width="800px" src="/static/img/blog/pillars-of-observability/network_map.png" />
                <v-img eager max-width="800px" src="/static/img/blog/pillars-of-observability/network_latency.png" />
            </template>
        </FailureScenario>

        <FailureScenario>
            <template #scenario>
                There is no network connectivity with the upstream service or database
            </template>
            <template #metrics>
                <div>
                    <MetricLink name="container_net_latency_seconds" exporter="node-agent"/>
                </div>
                <div>
                    <MetricLink name="container_net_tcp_failed_connects_total" exporter="node-agent"/>
                </div>
            </template>
            <template #threshold>
                network round-trip time (RTT) == NaN (ICMP probe failed)
            </template>
        </FailureScenario>

        <h2>Conclusion</h2>

        <p>
            It is difficult to accurately estimate the coverage of all possible issues by these inspections,
            but such analysis is an excellent starting point for troubleshooting.
        </p>

        <p>
            For example, if an application violates its SLOs, Coroot will send you an alert that already contains the list of the issues related to this app:
        </p>

        <v-card max-width="800px" class="pa-5 my-6">
            <v-img eager  class="ma-auto" src="/static/img/blog/observability-tax/slack.png" />
        </v-card>

        <p>
            Follow the instructions on our <router-link :to="{name: 'docs', params: {chapter: 'coroot-community-edition', group: 'getting-started', item: 'installation'}}">Getting started</router-link> page to try Coroot now.
            Not ready to Get started with Coroot? Check out our <a href="https://community-demo.coroot.com/" target="_blank">live demo</a>.
        </p>

        <p>
            If you like Coroot, give us a ⭐ on <a href="https://github.com/coroot/coroot" target="_blank">GitHub</a>️ or share your experience on
            <a href="https://www.g2.com/products/coroot/reviews" target="_blank">G2</a>.
        </p>

        <p>Any questions or feedback? Reach out to us on <a href="https://join.slack.com/t/coroot-community/shared_invite/zt-1gsnfo0wj-I~Zvtx5CAAb8vr~r~vecyw" target="_blank"><v-icon>mdi-slack</v-icon>Slack</a>.</p>





    </article>
</template>

<script>

import FailureScenario from "@/components/FailureScenario.vue";
import MetricLink from "@/components/MetricLink.vue";

export default {
    components: {MetricLink, FailureScenario},
}
</script>

<style scoped>
* >>> table {
    margin: 16px 0 !important;
}
* >>> td {
    padding: 4px 12px !important;
}
</style>
